import type {
	EFFeatureCapabilities,
	EFFeatureDockTarget,
	EFFeatureEligibilityRequirements,
	EFFeatureId,
	EFFeatureRenderer,
	EFFeatureSurface,
} from "@/features/runtime/FeatureTypes";
import type { EngagementSnapshotWidgetId } from "@/provider/EngagementProvider";

export type EFFeatureDefinition = {
	id: EFFeatureId;
	displayName: string;
	capabilities: EFFeatureCapabilities;
	eligibility: EFFeatureEligibilityRequirements;
	defaultDockTarget?: EFFeatureDockTarget;
	supportedSurfaces: readonly EFFeatureSurface[];
	snapshotWidgetId?: EngagementSnapshotWidgetId;
	renderer?: EFFeatureRenderer;
};

function uniqueSurfaces(
	surfaces: readonly EFFeatureSurface[]
): readonly EFFeatureSurface[] {
	return Array.from(new Set(surfaces));
}

function uniqueProviders(
	providers: readonly ("captainup")[]
): readonly ("captainup")[] {
	return Array.from(new Set(providers));
}

export function defineEFFeature(
	definition: EFFeatureDefinition
): EFFeatureDefinition {
	const normalizedSupportedSurfaces = uniqueSurfaces(
		definition.supportedSurfaces
	);

	if (normalizedSupportedSurfaces.length === 0) {
		throw new Error(
			`[FeatureDefinition] feature '${definition.id}' must declare at least one supported surface`
		);
	}

	if (
		definition.capabilities.usesDock &&
		!normalizedSupportedSurfaces.includes("dock")
	) {
		throw new Error(
			`[FeatureDefinition] feature '${definition.id}' declares usesDock=true but does not support the 'dock' surface`
		);
	}

	if (
		definition.capabilities.usesPopup &&
		!normalizedSupportedSurfaces.includes("popup")
	) {
		throw new Error(
			`[FeatureDefinition] feature '${definition.id}' declares usesPopup=true but does not support the 'popup' surface`
		);
	}

	if (definition.defaultDockTarget && !definition.capabilities.usesDock) {
		throw new Error(
			`[FeatureDefinition] feature '${definition.id}' declares a defaultDockTarget but does not use dock`
		);
	}

	if (
		definition.renderer?.renderDock &&
		!definition.capabilities.usesDock
	) {
		throw new Error(
			`[FeatureDefinition] feature '${definition.id}' declares renderDock but does not use dock`
		);
	}

	const normalizedSupportedProviders = definition.eligibility.supportedProviders
		? uniqueProviders(definition.eligibility.supportedProviders)
		: undefined;

	return {
		...definition,
		supportedSurfaces: normalizedSupportedSurfaces,
		eligibility: {
			...definition.eligibility,
			supportedProviders: normalizedSupportedProviders,
		},
	};
}